import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BottomNav from "@/components/BottomNav";
import BrandMark from "@/components/BrandMark";
import EditProfileSheet from "@/components/EditProfileSheet";
import Footer from "@/components/Footer";
import { IDS } from "@/constants/testIds";
import { LogOut, Pencil, Sparkles, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { setSessionToken } from "@/lib/api";

const STALE_PROFILE_DAYS = 180;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const NUDGE_STORAGE_KEY = "aina.stale_nudge_dismissed";

const daysSince = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / MS_PER_DAY);
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

export default function StyleProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showStaleNudge, setShowStaleNudge] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/style-profile");
    setData(data);
    const age = daysSince(data?.profile?.updated_at);
    const alreadyDismissed = localStorage.getItem(NUDGE_STORAGE_KEY) === "1";
    setShowStaleNudge(!alreadyDismissed && age !== null && age >= STALE_PROFILE_DAYS);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const deleteAccount = async () => {
    const first = window.confirm(
      "Delete your Aynora account?\n\nOnce deleted, your data will not be restored.\n\nThis will PERMANENTLY erase your profile, closet photos, wear history, chats, events, feedback and suggestions."
    );
    if (!first) return;
    const second = window.prompt('Type DELETE (uppercase) to confirm.');
    if (second !== "DELETE") return;
    try {
      await api.delete("/account");
      setSessionToken(null);
      window.alert("Your account and all data have been deleted.");
      window.location.href = "/";
    } catch (err) {
      console.error(err);
      window.alert("Could not delete account. Please try again.");
    }
  };

  const dismissNudge = () => {
    localStorage.setItem(NUDGE_STORAGE_KEY, "1");
    setShowStaleNudge(false);
  };

  const openEditFromNudge = () => {
    dismissNudge();
    setEditing(true);
  };

  const updatedAt = data?.profile?.updated_at;
  const updatedDaysAgo = daysSince(updatedAt);

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom, 0.75rem))" }}>
      <div className="max-w-md mx-auto px-6 pt-12">
        <BrandMark className="mb-4" />
        <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-stone-500">
          Style profile
        </div>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight tracking-tight text-stone-900 mb-2">
              {data?.profile?.name || user?.name}
            </h1>
            <p className="text-sm font-[Manrope] text-stone-600">{user?.email}</p>
          </div>
          <button
            data-testid="profile-edit-btn"
            onClick={() => setEditing(true)}
            className="mt-1 h-10 px-4 rounded-full bg-[#6B7A54] text-white flex items-center gap-2 text-sm font-[Manrope] font-medium shadow-[0_8px_24px_rgba(107, 122, 84,0.2)] active:scale-[0.97] transition-transform"
          >
            <Pencil size={14} strokeWidth={1.5} /> Edit
          </button>
        </div>

        {showStaleNudge && (
          <div
            data-testid="profile-stale-nudge"
            className="mt-6 rounded-2xl border border-[#6B7A54]/30 bg-[#F2EFEA] p-5"
          >
            <div className="flex items-start gap-3">
              <Sparkles size={18} strokeWidth={1.5} className="text-[#6B7A54] mt-0.5" />
              <div className="flex-1">
                <p className="font-[Cormorant_Garamond] text-lg italic text-stone-900 leading-snug">
                  Still accurate? Update your measurements for sharper suggestions.
                </p>
                <p className="mt-1 text-xs font-[Manrope] text-stone-500">
                  Last updated {formatDate(updatedAt)}
                  {updatedDaysAgo != null ? ` · ${updatedDaysAgo} days ago` : ""}
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    data-testid="profile-stale-update"
                    onClick={openEditFromNudge}
                    className="flex-1 bg-[#6B7A54] text-white rounded-full py-2.5 text-sm font-[Manrope] font-medium"
                  >
                    Update now
                  </button>
                  <button
                    data-testid="profile-stale-dismiss"
                    onClick={dismissNudge}
                    className="flex-1 bg-white border border-stone-200 text-stone-700 rounded-full py-2.5 text-sm font-[Manrope] font-medium"
                  >
                    Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {data?.profile?.body_type && (
          <Card>
            <Label>Silhouette</Label>
            <div className="font-[Cormorant_Garamond] text-2xl italic capitalize text-stone-900">
              {data.profile.body_type}
            </div>
            <p className="mt-2 text-sm font-[Manrope] text-stone-700 leading-relaxed">{data.profile.body_notes}</p>
          </Card>
        )}

        {data?.summary && (
          <Card>
            <Label>What I&rsquo;ve learned</Label>
            <p className="font-[Cormorant_Garamond] text-lg italic text-stone-900 leading-relaxed">
              {data.summary}
            </p>
          </Card>
        )}

        <div className="grid grid-cols-3 gap-3 mt-6">
          <Stat label="Closet" value={data?.closet_count ?? 0} />
          <Stat label="Wears" value={data?.wear_count ?? 0} />
          <Stat label="Loved" value={data?.thumbs_up ?? 0} />
        </div>

        <p
          data-testid="profile-last-updated"
          className="mt-6 text-center text-xs font-[Manrope] text-stone-500 tracking-wide"
        >
          Profile last updated {formatDate(updatedAt)}
        </p>

        <button
          data-testid={IDS.logoutBtn}
          onClick={doLogout}
          className="mt-6 w-full py-4 rounded-full border border-stone-300 text-stone-700 font-[Manrope] text-sm flex items-center justify-center gap-2 hover:bg-stone-50 transition-colors"
        >
          <LogOut size={14} strokeWidth={1.5} /> Sign out
        </button>

        <button
          data-testid="delete-account-btn"
          onClick={deleteAccount}
          className="mt-3 w-full py-4 rounded-full border border-red-200 text-red-600 font-[Manrope] text-sm flex items-center justify-center gap-2 hover:bg-red-50 transition-colors"
        >
          <Trash2 size={14} strokeWidth={1.5} /> Delete my account and all data
        </button>

        <Footer />
      </div>

      {editing && (
        <EditProfileSheet
          profile={data?.profile}
          onClose={() => setEditing(false)}
          onUpdated={load}
        />
      )}

      <BottomNav />
    </div>
  );
}

const Card = ({ children }) => (
  <div className="mt-4 bg-white border border-stone-100 rounded-2xl p-5 shadow-[0_2px_8px_rgba(28,25,23,0.04)]">
    {children}
  </div>
);
const Label = ({ children }) => (
  <div className="text-[10px] font-[Manrope] font-bold tracking-[0.25em] uppercase text-[#6B7A54] mb-2">{children}</div>
);
const Stat = ({ label, value }) => (
  <div className="bg-white border border-stone-100 rounded-2xl p-4 text-center">
    <div className="font-[Cormorant_Garamond] text-3xl text-stone-900">{value}</div>
    <div className="text-[10px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-stone-500 mt-1">{label}</div>
  </div>
);
